import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { addMinutes } from 'date-fns';
import { map, tap, catchError, of, switchMap, filter, mergeMap, take, EMPTY } from 'rxjs';

import { MeetingType } from '@ninety/platform/models/meetings/meeting-type.enum';
import { AgendaTableDataV2 } from '@ninety/platform/models/meetings-v2/agenda-table-data-v2';
import { teamAll, teamNone } from '@ninety/platform/models/team-list/team-list.model';
import {
  NotificationActions,
  selectCompanyId,
  selectCurrentUserEffectiveTimezone,
  selectLanguage,
  TeamUserSelectors,
} from '@ninety/platform/state';
import { environment } from '@ninety/ui/web/environments';
import { isOutlookCalendarProvider, NativeIntegrationCoreService } from '@ninety/web/pages/shared/integrations';

import { Cadence, MeetingSchedule } from '../../_models';
import { MeetingsV2Service } from '../../_services/meetings-v2.service';
import { MeetingService } from '../../_shared/services/meeting.service';
import { RecallCalendarService } from '../../_shared/services/recall-calendar.service';
import {
  DeleteDialogOptions,
  DeleteMeetingScheduleDialogComponent,
  DeleteMeetingScheduleDialogData,
} from '../../meetings-page/meeting-schedules/delete-meeting-schedule-dialog/delete-meeting-schedule-dialog.component';
// eslint-disable-next-line max-len
import { MeetingConfirmationModel } from '../../meetings-page/meeting-schedules/meeting-info-confirmation-dialog/meeting-confirmation.model';
// eslint-disable-next-line max-len
import { MeetingInfoConfirmationDialogComponent } from '../../meetings-page/meeting-schedules/meeting-info-confirmation-dialog/meeting-info-confirmation-dialog.component';
// eslint-disable-next-line max-len
import {
  ScheduleMeetingDialogComponent,
  ScheduleMeetingDialogData,
} from '../../meetings-page/meeting-schedules/schedule-meeting-dialog/schedule-meeting-dialog.component';
import {
  UpdateDialogOptions,
  UpdateMeetingScheduleDialogComponent,
  UpdateMeetingScheduleDialogData,
} from '../../meetings-page/meeting-schedules/update-meeting-schedule-dialog/update-meeting-schedule-dialog.component';
import { toAgendas, toCombinedAgendaOptionsTiered } from '../meetings-agenda-options.logic';
import { MeetingDialogActions, MeetingSchedulingActions } from '../meetings.actions';
import { MeetingsStateSelectors } from '../meetings.selectors';

const DEFAULT_ACTION_PARAMS = { actionText: 'X', config: { duration: 4_000, panelClass: 'plain-text-action' } };

// Recall can't reach a local dev origin, so fall back to this stage's public app origin for local
// development - mirrors ninetyMeetingUrlFor in meetings.effects.ts.
const LOCAL_DEV_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];

@Injectable()
export class MeetingSchedulingEffects {
  private actions$ = inject(Actions);

  private dialog = inject(MatDialog);
  private store = inject(Store);
  private meetingsV2Service = inject(MeetingsV2Service);
  private recallCalendarService = inject(RecallCalendarService);
  private meetingService = inject(MeetingService);
  private nativeIntegrationService = inject(NativeIntegrationCoreService);

  openScheduleMeetingDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingSchedulingActions.openScheduleMeetingDialog),
        tap(({ teamId }) => {
          const data: ScheduleMeetingDialogData = { teamId: teamId ?? null };
          this.dialog.open(ScheduleMeetingDialogComponent, { width: '580px', data });
        })
      ),
    { dispatch: false }
  );
  openMeetingInfoConfirmationDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MeetingSchedulingActions.addMeetingScheduleSuccess,
          MeetingSchedulingActions.openMeetingConfirmationDialog
        ),
        filter(enabled => !!enabled),
        concatLatestFrom(() => [
          this.store.select(MeetingsStateSelectors.selectSelectedTeamName),
          this.store.select(MeetingsStateSelectors.selectAgendas),
          this.store.select(selectCurrentUserEffectiveTimezone),
          this.store.select(MeetingsStateSelectors.selectCombinedAgendaOptionsTiered),
          this.store.select(MeetingsStateSelectors.selectAgendaDerivationContext),
        ]),
        switchMap(([action, filterBarTeamName, filterBarAgendas, timezone, filterBarAgendaOptions, agendaContext]) => {
          const { schedule } = action;
          const team = 'team' in action ? action.team : undefined;

          const teamName = team?.name ?? filterBarTeamName;
          const agendas = team ? toAgendas({ ...agendaContext, team }) : filterBarAgendas;
          const formAgendaOptions = team
            ? toCombinedAgendaOptionsTiered({ ...agendaContext, team })
            : filterBarAgendaOptions;

          return this.store.select(TeamUserSelectors.selectTeamUsers(schedule.teamId)).pipe(
            take(1),
            tap(users => {
              let agenda: AgendaTableDataV2 = null;
              if (schedule.meetingType === MeetingType.custom) {
                agenda = agendas.find(agenda => agenda.id === schedule.agendaId);
              } else {
                agenda = agendas.find(agenda => agenda.id === schedule.meetingType);
              }

              if (!agenda) {
                return;
              }
              const formAgendaId =
                schedule.meetingType === MeetingType.custom ? schedule.agendaId : schedule.meetingType;
              const formAgendaOption = formAgendaOptions.find(agendaOption => agendaOption.id === formAgendaId);

              let startDate: Date;
              if ((schedule as MeetingSchedule).scheduledDate !== undefined) {
                startDate = new Date((schedule as MeetingSchedule).scheduledDate);
              } else {
                startDate = new Date(schedule.cadenceStartDate);
              }

              // Duration in seconds. Round up to the nearest 15 minute interval
              const roundedDuration = Math.ceil(agenda.totalDuration / (60 * 15)) * 15;
              const data: MeetingConfirmationModel = {
                meetingTitle: formAgendaOption?.displayText || agenda.title,
                meetingStartDate: startDate,
                meetingEndDate: addMinutes(startDate, roundedDuration),
                teamName,
                cadence: schedule.cadence,
                invitees: users.map(user => user.primaryEmail),
                duration: roundedDuration,
                userTimezone: timezone,
              };

              this.dialog.open(MeetingInfoConfirmationDialogComponent, { data });
            })
          );
        })
      ),
    { dispatch: false }
  );

  addMeetingSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.addMeetingSchedule),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectTeamId),
        this.store.select(selectCompanyId),
      ]),
      switchMap(([{ schedule, team }, meetingsFilterTeamId, companyId]) => {
        const teamId = team?._id ?? meetingsFilterTeamId;

        if (!teamId || teamId === teamAll._id || teamId === teamNone._id) {
          return of(
            MeetingSchedulingActions.addMeetingScheduleFailure({
              error: new Error(`Cannot schedule a meeting for team "${teamId}"`),
              message: 'Add Schedule failed',
            })
          );
        }

        return this.meetingsV2Service.addMeetingSchedule(companyId, { ...schedule, teamId }).pipe(
          map(scheduleResult => MeetingSchedulingActions.addMeetingScheduleSuccess({ schedule: scheduleResult, team })),
          catchError((error: unknown) =>
            of(MeetingSchedulingActions.addMeetingScheduleFailure({ error, message: 'Add Schedule failed' }))
          )
        );
      })
    )
  );

  addMeetingScheduleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.addMeetingScheduleSuccess),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      map(([_, lang]) =>
        NotificationActions.notifyV2({
          message: `${lang?.meeting?.item || 'Meeting'} scheduled`,
          params: DEFAULT_ACTION_PARAMS,
        })
      )
    )
  );

  // Fire-and-forget: hands the picked meeting link off to meeting-agent so it can schedule a
  // Recall bot for this schedule. Only runs when the user actually picked a calendar event -
  // see docs/calendar-scheduling-architecture.md in the meeting-agent repo (diagram 2).
  scheduleRecallBot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.addMeetingScheduleSuccess),
      filter(({ schedule }) => !!schedule.meetingLink && !!schedule._id),
      mergeMap(({ schedule }) => {
        const activeIntegration = this.nativeIntegrationService.activeCalendarIntegration();
        const calendarProvider = activeIntegration
          ? isOutlookCalendarProvider(activeIntegration)
            ? 'microsoft-outlook'
            : 'google-calendar'
          : undefined;

        return this.recallCalendarService
          .scheduleBot(schedule._id, schedule.meetingLink, this.ninetyMeetingsUrl(), calendarProvider)
          .pipe(
            switchMap(result => {
              if (result.status === 'pendingSync') {
                return of(
                  NotificationActions.notifyV2({
                    message: "Your calendar is still syncing - Maz wasn't scheduled yet, try saving again shortly.",
                    params: DEFAULT_ACTION_PARAMS,
                  })
                );
              }
              if (result.status === 'notConnected') {
                return of(
                  NotificationActions.notifyV2({
                    message: 'Connect your calendar in Settings to auto-schedule Maz for this meeting.',
                    params: DEFAULT_ACTION_PARAMS,
                  })
                );
              }
              return EMPTY;
            }),
            catchError((error: unknown) =>
              of(
                NotificationActions.notifyError({
                  error,
                  message: 'Could not schedule Maz for this meeting.',
                })
              )
            )
          );
      })
    )
  );

  // Best-effort, fire-and-forget: tells meeting-agent which meeting instance a schedule's
  // calendar-scheduled bot (if any) now belongs to, so its status webhook can resolve a meetingId
  // for it - the bot itself carries none, since it was scheduled before this meeting existed. Runs
  // on every schedule start, not just ones known to have a bot; meeting-agent no-ops harmlessly
  // when there's nothing to link. Failure isn't surfaced - the meeting itself already started
  // successfully, and losing live bot-status correlation isn't worth a user-facing error for.
  //
  // Also force a bot-status re-fetch once linking succeeds: initializeMeeting's own refresh (fired
  // by the same createMeetingSuccess navigation) can race ahead of meeting-agent processing this
  // link and come back empty. If the bot was already recording before this meeting started, no
  // further status *transition* occurs to correct that via the realtime push, so without this
  // retry the notetaker control would show "no bot" indefinitely.
  linkScheduledMeetingBot$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingDialogActions.createMeetingSuccess),
        filter(({ meetingScheduleId, meeting }) => !!meetingScheduleId && !!meeting._id),
        mergeMap(({ meetingScheduleId, meeting }) =>
          this.recallCalendarService.linkMeeting(meetingScheduleId!, meeting._id).pipe(
            tap(() => this.meetingService.refreshMeetingBotStatus(meeting, !!meeting.botLinked)),
            catchError(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );

  // No specific meeting instance exists yet for an auto-scheduled bot (it may join before anyone
  // clicks "Start meeting" in Ninety), so this links to the team's upcoming meetings list rather
  // than a specific /meeting/:id session - see ninetyMeetingUrlFor in meetings.effects.ts for the
  // in-progress-session equivalent.
  private ninetyMeetingsUrl(): string {
    const ninetyOrigin = LOCAL_DEV_HOSTNAMES.includes(window.location.hostname)
      ? environment.publicAppOrigin
      : window.location.origin;
    return `${ninetyOrigin}/meetings`;
  }

  getMeetingSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingSchedulingActions.getMeetingSchedules,
        MeetingSchedulingActions.addMeetingScheduleSuccess,
        MeetingSchedulingActions.deleteMeetingScheduleSuccess,
        MeetingSchedulingActions.updateMeetingScheduleSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectTeamId),
        this.store.select(selectCompanyId),
      ]),
      switchMap(([action, meetingsFilterTeamId, companyId]) => {
        const teamId = 'schedule' in action ? (action.schedule?.teamId ?? meetingsFilterTeamId) : meetingsFilterTeamId;

        if (!teamId || teamId === teamAll._id || teamId === teamNone._id) return EMPTY;

        return this.meetingsV2Service.getMeetingSchedules({ teamId, companyId }).pipe(
          map(schedules => MeetingSchedulingActions.getMeetingSchedulesSuccess({ schedules })),
          catchError((error: unknown) =>
            of(MeetingSchedulingActions.getMeetingSchedulesFailure({ error, message: 'Get Schedules failed' }))
          )
        );
      })
    )
  );

  getMeetingSchedulesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingSchedulingActions.getMeetingSchedulesSuccess),
        map(({ schedules }) => this.meetingsV2Service.setMeetingSchedules(schedules))
      ),
    { dispatch: false }
  );

  openDeleteMeetingScheduleDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.openDeleteMeetingScheduleDialog),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      switchMap(([{ schedule }, language]) =>
        this.dialog
          .open<DeleteMeetingScheduleDialogComponent, DeleteMeetingScheduleDialogData, DeleteDialogOptions>(
            DeleteMeetingScheduleDialogComponent,
            {
              data: {
                meetingLanguage: language?.meeting || null,
                cadence: schedule.cadence,
                isRepeating: schedule.cadence !== Cadence.none,
              },
            }
          )
          .afterClosed()
          .pipe(
            filter(deleteOptions => !!deleteOptions),
            map(deleteOptions => {
              let canceledForDate: Date = null;
              if (deleteOptions.isRepeating && deleteOptions.deleteAll === false) {
                //a specific date when deleting schedule for a specific period
                canceledForDate = new Date(schedule.scheduledDate);
              }
              return MeetingSchedulingActions.deleteMeetingSchedule({
                scheduleId: schedule._id,
                isRepeating: deleteOptions.isRepeating,
                deleteAll: deleteOptions.deleteAll,
                ...(canceledForDate && { canceledForDate }),
              });
            })
          )
      )
    )
  );

  deleteMeetingSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.deleteMeetingSchedule),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectTeamId),
        this.store.select(selectCompanyId),
      ]),
      mergeMap(([{ scheduleId, deleteAll, isRepeating, canceledForDate }, teamId, companyId]) =>
        this.meetingsV2Service
          .deleteMeetingSchedule(scheduleId, {
            teamId,
            companyId,
            deleteAll,
            isRepeating,
            ...(canceledForDate && {
              canceledForDate: new Date(canceledForDate).toISOString(),
            }),
          })
          .pipe(
            map(() => MeetingSchedulingActions.deleteMeetingScheduleSuccess({ scheduleId, canceledForDate })),
            catchError((error: unknown) =>
              of(MeetingSchedulingActions.deleteMeetingScheduleFailure({ error, message: 'Delete Schedule failed' }))
            )
          )
      )
    )
  );

  // Best-effort, fire-and-forget: tells meeting-agent to unschedule this schedule's calendar bot
  // (if any) once the schedule itself is actually gone - not on a single-occurrence cancellation
  // (canceledForDate set), since the schedule and its bot both still exist for future occurrences
  // in that case. Failure isn't surfaced - the schedule itself already deleted successfully, and an
  // orphaned Recall bot can still be cleaned up later (e.g. by re-saving the schedule).
  unscheduleRecallBot$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingSchedulingActions.deleteMeetingScheduleSuccess),
        filter(({ canceledForDate }) => !canceledForDate),
        mergeMap(({ scheduleId }) => this.recallCalendarService.unscheduleBot(scheduleId).pipe(catchError(() => EMPTY)))
      ),
    { dispatch: false }
  );

  notifyOnDeleteMeetingScheduleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.deleteMeetingScheduleSuccess),
      map(() =>
        NotificationActions.notifyV2({
          message: 'Delete successful',
          params: DEFAULT_ACTION_PARAMS,
        })
      )
    )
  );

  openUpdateMeetingScheduleDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.openUpdateMeetingScheduleDialog),
      concatLatestFrom(() => [
        this.store.select(selectLanguage),
        this.store.select(MeetingsStateSelectors.selectNonObserverUsersForTeamAndCoaches),
      ]),
      mergeMap(([{ schedule }, language, users]) =>
        this.dialog
          .open<UpdateMeetingScheduleDialogComponent, UpdateMeetingScheduleDialogData, UpdateDialogOptions>(
            UpdateMeetingScheduleDialogComponent,
            {
              width: '580px',
              maxWidth: '580px',
              data: {
                meetingLanguage: language?.meeting || null,
                schedule,
                users,
              },
            }
          )
          .afterClosed()
          .pipe(
            filter(updateOptions => !!updateOptions),
            map(updateOptions => MeetingSchedulingActions.updateMeetingSchedule({ updateOptions }))
          )
      )
    )
  );

  updateMeetingSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.updateMeetingSchedule),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectTeamId),
        this.store.select(selectCompanyId),
      ]),
      switchMap(([{ updateOptions }, teamId, companyId]) => {
        const { scheduleId, isRepeating, updateAll, update } = updateOptions;

        return this.meetingsV2Service
          .updateMeetingSchedule(scheduleId, update, {
            teamId,
            companyId,
            isRepeating,
            updateAll,
          })
          .pipe(
            map(() => MeetingSchedulingActions.updateMeetingScheduleSuccess()),
            catchError((error: unknown) =>
              of(
                MeetingSchedulingActions.updateMeetingScheduleFailure({
                  error,
                  message: 'Update Schedule failed',
                })
              )
            )
          );
      })
    )
  );

  notifyOnUpdateMeetingScheduleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.updateMeetingScheduleSuccess),
      map(() =>
        NotificationActions.notifyV2({
          message: 'Update successful',
          params: DEFAULT_ACTION_PARAMS,
        })
      )
    )
  );

  updateMeetingScheduleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingSchedulingActions.updateMeetingScheduleFailure,
        MeetingSchedulingActions.deleteMeetingScheduleFailure,
        MeetingSchedulingActions.getMeetingSchedulesFailure,
        MeetingSchedulingActions.addMeetingScheduleFailure
      ),
      map(({ error, message }) => NotificationActions.notifyError({ error, message }))
    )
  );
}
